import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { collection, getDocs, query, where } from 'firebase/firestore';
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import ResponsiveJoinedAppBar from "../components/AppBarLoggedIn";
import EventHero from "../components/EventHero";
import EventListRelated from "../components/EventListRelated";
import EventMap from '../components/EventMap';
import EventSpeakers from '../components/explore/EventSpeakers';
import EventSponsors from '../components/explore/EventSponsors';
import EventSchedule from '../components/explore/EventSchedule';
import Footer from "../components/Footer";
import { db } from '../firebase';
import TicketOptions from "../components/ticket/TicketOptions";

// Create Theme Context
const ThemeContext = createContext();

const EventPage = () => {
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { eventCode } = useParams();  // Capture the eventCode from the URL
  const [eventDetails, setEventDetails] = useState(null);
  const [eventDocId, setEventDocId] = useState(null);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    if (eventCode) {
      fetchEvent(eventCode);
    }
  }, [eventCode]);

  const fetchEvent = async (code) => {
    try {
      setError('');
      setEventDetails(null);
      setEventDocId(null);

      // Find the event document by eventCode
      const eventsRef = collection(db, 'events');
      const eventQuery = query(eventsRef, where('eventCode', '==', code));
      const eventSnapshot = await getDocs(eventQuery);

      if (!eventSnapshot.empty) {
        // Assume the first matching event document
        const eventDoc = eventSnapshot.docs[0];
        const eventData = eventDoc.data();

        // Setting the page title 
        { document.title = "nKuvu - " + eventData.eventName }

        setEventDocId(eventDoc.id);  // Store document ID for post retrieval
        setEventDetails({
          eventId: eventDoc.id,
          eventImage: eventData.eventImageURL,
          eventName: eventData.eventName,
          eventCode: eventData.eventCode,
          eventDescription: eventData.eventDescription,
          eventOwnerName: eventData.eventOwnerName,
          eventLatitude: eventData.eventLatitude,
          eventLongitude: eventData.eventLongitude,
          eventCity: eventData.eventCity,
          eventLocation: eventData.eventLocation,
          eventStartDate: eventData.eventStartDate,
        });
      } else {
        setError('No event found with that event code.');
      }
    } catch (err) {
      console.error("Error fetching event:", err);
      setError('Failed to retrieve event data.');
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "grey.200", }}>
      <ResponsiveJoinedAppBar />

      <Box sx={{ bgcolor: '#fef8e8', py: 2, mt: "auto" }}>
        <EventHero eventDetails={eventDetails} />
      </Box>

      <Box sx={{ bgcolor: 'background.paper', width: "100%", textAlign: "center", }} >
        <EventSchedule eventDocId={eventDocId} />
      </Box>

      {/* Speakers */}
      <Box sx={{ bgcolor: 'background.paper', }} >
        <EventSpeakers eventDocId={eventDocId} />
      </Box>

      {/* Sponsors */}
      <Box sx={{ bgcolor: 'background.paper', py: 2, }}> <EventSponsors eventDocId={eventDocId} />
      </Box>

      {/* Maps*/}
      <Box sx={{ bgcolor: 'background.paper', textAlign: "center", py: 2, mt: "auto" }}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          The Venue
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 2, mb: 4 }}>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit.
        </Typography>

        <EventMap eventDetails={eventDetails} />
      </Box>
      {/* Related Events */}
      <Box sx={{ bgcolor: 'background.paper', textAlign: "center", py: 2, mt: "auto" }}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Related
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 2, mb: 4 }}>
          Join our successful sponsors. Show of your brand along with successful business.
        </Typography>

        <EventListRelated />
      </Box>

      {/* Footer */}
      <Footer />
      {/* <Box component="footer" sx={{ bgcolor: 'background.paper', textAlign: "center", py: 2, mt: "auto" }}>
        <Typography variant="body2" color="text.primary">© 2025 nKuvu</Typography>
      </Box> */}
    </Box>
  );
};

// Theme Provider Wrapper
const App = () => {
  const [darkMode, setDarkMode] = useState(false);

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
    },
  });

  const toggleDarkMode = () => setDarkMode(!darkMode);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <ThemeProvider theme={theme}>
        <EventPage />
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default App;
